import { WizardAction } from './logger.wizard-event';
import { Inject, Injectable, Injector } from '@angular/core';
import { ILoggingContext } from 'app/interfaces/IPortalServiceResult';
import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { Metamodel } from 'app/model/base/model';

import { EventRow } from './logger.event-row';
import { MetamodelCrudEvent } from './logger.metamodel-crud-event';
import { Named } from 'app/model/base/named';
import { ILogger } from 'app/logger/ILogger';
import { LoggerBaseService } from './logger-base.service';
import { WorkspaceLoggerService } from './workspace-logger.service';
import { AdfLoggerService } from './adf-logger.service';
import { WorkspaceUrlInformationService } from 'app/services/workspace-url-information.service';
import { MetricRow } from './logger.metric-row';
import { UrlInformationBaseService } from 'app/services/url-information-base.service';
import { Subject } from 'rxjs';
import { TridentLoggerService } from 'app/services/trident/trident-logger.service';
import { IResourceContext, RESOURCE_CONTEXT_DATA_TOKEN } from 'app/multi-contexts/resource-context.typings';
import { assertPositive } from 'app/utils/assertUtils';

export interface IModelChanged {
    changeType: 'Create' | 'Update' | 'Fetched' | 'Delete';
    subChangeType?: 'rename';
    named: Named;
}

@Injectable({
    providedIn: 'root'
})
export class LoggerService implements ILogger {
    public readonly modelChanged$: Subject<IModelChanged[]> = new Subject<IModelChanged[]>();
    public readonly urlProvider: UrlInformationBaseService;
    private _logger: LoggerBaseService;

    constructor(
        @Inject(RESOURCE_CONTEXT_DATA_TOKEN) contextData: IResourceContext,
        _urlService: FactoryUrlInformationService,
        _wsUrlService: WorkspaceUrlInformationService,
        _injector: Injector
    ) {
        if (multiContextRootModuleAsEntry) {
            assertPositive(
                contextData.contextType !== 'root',
                'LoggerService could only be initialized in the non-root context.'
            );
        }

        this._logger = __IS_A365__
            ? new WorkspaceLoggerService(_injector, _wsUrlService)
            : __IS_FABRIC_DPE__ || __IS_FABRIC_ADF__ || __IS_FABRIC_MDF__
                ? new TridentLoggerService(_injector)
                : new AdfLoggerService(_injector, _urlService);

        this.modelChanged$.subscribe((changes: IModelChanged[]) => {
            changes.forEach((c) => {
                if (c.changeType === 'Create' || c.changeType === 'Update' || c.changeType === 'Delete') {
                    const concept = Metamodel.modelOf(c.named).concept;
                    this.logEvent(
                        new MetamodelCrudEvent(
                            c.changeType,
                            c.named.name || '',
                            Metamodel.modelOf(c.named).name,
                            concept ? concept.name : ''
                        )
                    );
                }
            });
        });
        this.urlProvider = __IS_A365__ ? _wsUrlService : _urlService;
    }

    public initializeLoggingContext(): void {
        this._logger.initializeLoggingContext();
    }

    public async getLoggingContextAsync(): Promise<ILoggingContext> {
        return this._logger.getLoggingContextAsync();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public log(componentName: string, message: string, properties?: { [key: string]: any }): void {
        this._logger.log(componentName, message, properties);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public error(componentName: string, message: any, properties?: { [key: string]: string }): void {
        this._logger.error(componentName, message, properties);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public warn(componentName: string, message: any, properties?: { [key: string]: any }): void {
        this._logger.warn(componentName, message, properties);
    }

    public logEvent(event: EventRow): void {
        this._logger.logEvent(event);
    }

    public logMetric(metric: MetricRow): void {
        this._logger.logMetric(metric);
    }

    public logWizardEvent(
        step: string,
        substep: string,
        page: string,
        action: WizardAction,
        message?: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalInfo?: { [key: string]: any }
    ): void {
        this._logger.logWizardEvent(step, substep, page, action, message, additionalInfo);
    }

    public logPageView(): void {
        this._logger.logPageView();
    }

    /**
     * This only works in dev mode
     */
    public debug(componentName: string, message: string, ...properties: unknown[]): void {
        this._logger.debug(componentName, message, ...properties);
    }

    public addTrackingParameter(key: string): void {
        this._logger.addTrackingParameter(key);
    }

    public removeTrackingParameter(key: string): void {
        this._logger.removeTrackingParameter(key);
    }

    public flush(): void {
        this._logger.flush();
    }
}
